import type { AdCardState } from '../creatives/components/AdLibraryShell';
import type { WebsiteCardSummary } from '../website/card-summary';

/**
 * What a Marketing area's hub card says, from the area's library: how many live (non-archived) ads it holds, a
 * thumbnail of the newest one that has rendered, and a chip while one is generating (else while some are drafts).
 * Pure: copy is an id, mapped to its key per area in `ad-card-copy.ts`.
 */

/** One live ad, as its library's card shows it: its state and its poster ('' while nothing has rendered). */
export type AdCardItem = { state: AdCardState; poster: string };

export type AdCardInput =
  | { status: 'loading' }
  | { status: 'failed' }
  /** The live ads, newest first. */
  | { status: 'ready'; items: AdCardItem[] };

export type AdCardCopy =
  | { id: 'count'; n: number }
  | { id: 'none' }
  | { id: 'generating' }
  | { id: 'drafts'; n: number };

export const deriveAdCardSummary = (input: AdCardInput): WebsiteCardSummary<AdCardCopy> => {
  // The card shows no actions (the whole card opens the area), so the summary offers none.
  const none = { primary: null, secondary: null };
  if (input.status === 'loading') return { chip: null, body: { kind: 'loading' }, ...none };
  if (input.status === 'failed') return { chip: null, body: { kind: 'failed' }, ...none };
  const { items } = input;
  if (items.length === 0) return { chip: null, body: { kind: 'get-started', line: { id: 'none' } }, ...none };

  const drafts = items.filter((item) => item.state === 'draft').length;
  const chip: WebsiteCardSummary<AdCardCopy>['chip'] = items.some((item) => item.state === 'generating')
    ? { tone: 'working', copy: { id: 'generating' } }
    : drafts > 0
      ? { tone: 'neutral', copy: { id: 'drafts', n: drafts } }
      : null;
  const poster = items.find((item) => item.poster !== '')?.poster;
  return {
    chip,
    body: { kind: 'context', line: { id: 'count', n: items.length }, ...(poster ? { art: { thumbUrl: poster } } : {}) },
    ...none,
  };
};
